import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../../environments/environment';

export interface RelatedFiscalDocument {
  id: string;
  number: string;
}

export interface InvoiceBookItem {
  id: string;
  invoiceNumber: string;
  number?: string;
  posCode?: number;
  documentTypeCode: string;
  issueDate: string;
  customerName: string;
  customerDocNumber: string;
  subtotal: number;
  taxTotal: number;
  total: number;
  status: string;
  type: string;
  /** Presente en detalle (`InvoiceDetails`) y en algunos listados. */
  invoiceType?: string;
  currency: string;
  cancelledAt?: string;
  cancelReason?: string;
  documentType?: string; // "Invoice" o "CreditNote"
  reversed?: boolean;
  authorizationStatus?: number;
  authorizationCode?: string | null;
  authorizationExpiration?: string | null;
  originalInvoiceId?: string | null;
  originalInvoiceNumber?: string | null;
  relatedCreditNotes?: RelatedFiscalDocument[];
  /** 0 = Electrónico, 1 = Manual/contingencia (SalesPointEmissionKind). */
  emissionKind?: number;
}

export interface InvoiceDetails {
  id: string;
  number: string;
  posCode?: number;
  documentTypeCode: string;
  issueDate: string;
  customerId: string;
  customerName: string;
  customerDocNumber: string;
  subscriptionId?: string;
  reversesPlanCoverage?: boolean;
  subtotal: number;
  taxTotal: number;
  total: number;
  status: string;
  paymentMethodName?: string;
  paymentMethodId?: string;
  currency: string;
  invoiceType: string;
  items: InvoiceItemDetailsDto[];
  cancelledAt?: string;
  cancelReason?: string;
  reversed?: boolean;
  tenantTaxMode?: string;
  alicIvas?: FiscalVatAlicDto[];
  tributos?: FiscalTributeDto[];
  authorizationStatus?: number;
  authorizationCode?: string | null;
  authorizationExpiration?: string | null;
  relatedCreditNotes?: RelatedFiscalDocument[];
  authorizationQrUrl?: string | null;
  authorizationQrAvailable?: boolean;
  showTaxBreakdownToCustomer?: boolean;
}

export interface InvoiceLineTaxDetailsDto {
  taxName: string;
  ratePercent: number;
  isInclusive: boolean;
  taxType: string;
  baseAmount: number;
  taxAmount: number;
  fiscalCode?: string;
  fiscalSystem?: string;
}

export interface FiscalVatAlicDto {
  fiscalCode?: string;
  baseAmount: number;
  amount: number;
}

export interface FiscalTributeDto {
  fiscalCode?: string;
  fiscalSystem?: string;
  baseAmount: number;
  amount: number;
}

export interface InvoiceItemDetailsDto {
  id: string;
  description: string;
  quantity: number;
  unitPrice: number;
  subtotal: number;
  taxRate: number;
  taxAmount: number;
  total: number;
  lineTaxes?: InvoiceLineTaxDetailsDto[];
}

export interface PaginatedInvoices {
  items: InvoiceBookItem[];
  totalCount: number;
}

export interface CreateManualInvoiceRequest {
  customerId: string;
  invoiceType: 'CASH' | 'CURRENT_ACCOUNT';
  documentTypeCode: string;
  posCode: string;
  invoiceNumber: number;
  issueDate: string;
  currency: string;
  subtotal: number;
  taxTotal: number;
  total: number;
  items: InvoiceItemDto[];
  payment?: PaymentInfo;
  cashBoxOpeningId?: string;
  receiverDocType?: string;
  receiverDocNumber?: string;
}

export interface InvoiceItemDto {
  description: string;
  quantity: number;
  unitPrice: number;
  subtotal: number;
  taxRate: number;
  taxAmount: number;
  total: number;
}

export interface PaymentInfo {
  paymentMethod: string;
  amount: number;
  paymentDate: string;
  reference?: string;
}

export interface NextInvoiceNumber {
  documentTypeCode: string;
  posCode: string;
  number: number;
  /** Número visible según plantilla del país / serie (ej. 0001-00000055). */
  formattedFiscalNumber: string;
  /** None | Ticket | Electronic — modo fiscal del tenant (facturación manual). */
  tenantTaxMode?: string;
}

export interface ApiResponse<T> {
  success: boolean;
  code: string;
  reason: string;
  requestResult: T;
  body: T;
}

@Injectable({
  providedIn: 'root'
})
export class InvoicesBookService {
  private apiUrl = environment.apiBaseUrl;

  constructor(private http: HttpClient) { }

  getInvoicesBook(
    pageNumber: number = 1,
    pageSize: number = 50,
    fromDate?: string,
    toDate?: string,
    documentTypeCode?: string,
    status?: string,
    searchTerm?: string,
    documentTypeFilter?: string
  ): Observable<ApiResponse<PaginatedInvoices>> {
    let params = new HttpParams()
      .set('pageNumber', pageNumber.toString())
      .set('pageSize', pageSize.toString());

    if (fromDate) params = params.set('fromDate', fromDate);
    if (toDate) params = params.set('toDate', toDate);
    if (documentTypeCode) params = params.set('documentTypeCode', documentTypeCode);
    if (status) params = params.set('status', status);
    if (searchTerm) params = params.set('searchTerm', searchTerm);
    if (documentTypeFilter) params = params.set('documentTypeFilter', documentTypeFilter);

    return this.http.get<ApiResponse<PaginatedInvoices>>(
      `${this.apiUrl}/api/v1/Invoices/book`,
      { params }
    );
  }

  getNextInvoiceNumber(
    customerId: string,
    cashBoxOpeningId: string,
    reserveNumber: boolean = false
  ): Observable<ApiResponse<NextInvoiceNumber>> {
    const params = new HttpParams()
      .set('customerId', customerId)
      .set('cashBoxOpeningId', cashBoxOpeningId)
      .set('reserveNumber', reserveNumber.toString());

    return this.http.get<ApiResponse<NextInvoiceNumber>>(
      `${this.apiUrl}/api/v1/Invoices/next-manual-number`,
      { params }
    );
  }

  createManualInvoice(request: CreateManualInvoiceRequest): Observable<ApiResponse<any>> {
    return this.http.post<ApiResponse<any>>(
      `${this.apiUrl}/api/v1/Invoices/manual`,
      request
    );
  }

  getInvoiceDetails(invoiceId: string): Observable<ApiResponse<InvoiceDetails>> {
    return this.http.get<ApiResponse<InvoiceDetails>>(
      `${this.apiUrl}/api/v1/Invoices/${invoiceId}`
    );
  }

  getInvoiceDueDates(invoiceId: string): Observable<any> {
    return this.http.get<any>(
      `${this.apiUrl}/api/v1/Invoices/${invoiceId}/due-dates`
    );
  }

}
